import { useMemo } from 'react';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';

/** Team module gates: owner has full access; employees need explicit Team actions. */
export function useTeamAccess() {
  const { entitlements, loading: entLoading } = usePlanEntitlements();
  const allowEmployees = entitlements?.allow_employees !== false;
  const isOwner = entitlements?.is_owner === true;

  const canViewModule = useModulePermission('Team', 'view');
  const canInviteAction = useModulePermission('Team', 'invite');
  const canAssignBranches = useModulePermission('Team', 'assign_branches');
  const canEditPermissions = useModulePermission('Team', 'edit_permissions');
  const canRevoke = useModulePermission('Team', 'revoke');
  const canHistory = useModulePermission('Team', 'history');
  const canExport = useModulePermission('Team', 'export');

  return useMemo(() => {
    const planOk = allowEmployees;
    const canView = planOk && (isOwner || canViewModule);
    return {
      entLoading,
      entitlements,
      isOwner,
      allowEmployees: planOk,
      canViewTeam: canView,
      /** Legacy alias: any team management beyond view. */
      canManageTeam: canView && (isOwner || canEditPermissions || canInviteAction || canRevoke),
      canInvite: planOk && (isOwner || canInviteAction),
      canAssignBranches: planOk && (isOwner || canAssignBranches),
      canEditPermissions: planOk && (isOwner || canEditPermissions),
      canRevoke: planOk && (isOwner || canRevoke),
      canHistory: planOk && (isOwner || canHistory || canExport),
      canExportHistory: planOk && (isOwner || canExport),
    };
  }, [
    allowEmployees,
    canAssignBranches,
    canEditPermissions,
    canExport,
    canHistory,
    canInviteAction,
    canRevoke,
    canViewModule,
    entLoading,
    entitlements,
    isOwner,
  ]);
}
